import { computed, DestroyRef, inject, Service, signal } from '@angular/core';

import { ActionState } from '@shared/state/async-action';
import { PlatformService } from '@core/platform/platform.service';
import { OrganizationsRepository } from '../domain/interfaces/organizations.interface';
import {
  EMPTY_HEALTH_CATALOG,
  EMPTY_HEALTH_SYNC_RESULT,
  OrgHealthCatalog,
  OrgHealthSample,
  OrgHealthSyncResult,
  chunkHealthSamples,
  healthSyncLanded,
} from '../domain/models/org-health.model';
import { OrgError } from '../domain/org.errors';
import { OrgErrorMapper } from './org-error.mapper';
import { destroyNotifier, firstValueFromScoped, runOrgAction } from './org-action';

/**
 * OrgHealthFacade — `GET /health/metrics` · `POST /health/samples` (§5).
 *
 * ── Scope on the web ────────────────────────────────────────────────────────
 * §5 is headed "mobile only", and it is: the payload is one daily total per
 * metric per day, read from HealthKit / Health Connect / صحتي. A browser has no
 * such bridge, so this facade ships the CATALOGUE read and the SUBMIT path
 * (which work today for `source: 'manual'` rows and for anything a future
 * companion app hands us) and stops there. It does not pretend to read a device
 * the web cannot see — see ESCALATIONS.md E19.
 *
 * What it does own is the rule the status code hides: PARTIAL ACCEPTANCE is the
 * design, and `accepted: 0` is a perfectly ordinary 200 (trap 11). Nothing here
 * infers success from a 2xx — {@link lastResult} is read, and
 * {@link healthSyncLanded} is what "it worked" means.
 */
@Service({ autoProvided: false })
export class OrgHealthFacade {
  private readonly repo = inject(OrganizationsRepository);
  private readonly errors = inject(OrgErrorMapper);
  private readonly platform = inject(PlatformService);
  private readonly destroyed = destroyNotifier(inject(DestroyRef));

  private readonly _catalog = signal<OrgHealthCatalog>(EMPTY_HEALTH_CATALOG);
  private readonly _result = signal<OrgHealthSyncResult>(EMPTY_HEALTH_SYNC_RESULT);
  private readonly _catalogError = signal<OrgError | null>(null);
  private readonly _syncError = signal<OrgError | null>(null);
  private readonly _loadedAt = signal(0);

  readonly catalogLoad = new ActionState('reference_portal_org_health_catalog_failed', 'GET /health/metrics');
  readonly sync = new ActionState('reference_portal_org_health_sync_failed', 'POST /health/samples');

  readonly catalog = this._catalog.asReadonly();
  readonly lastResult = this._result.asReadonly();
  readonly catalogError = this._catalogError.asReadonly();
  readonly syncError = this._syncError.asReadonly();

  readonly metrics = computed(() => this._catalog().metrics);
  /** Ready-to-render privacy sentence — shown wherever samples are offered. */
  readonly privacyNote = computed(() => this._catalog().privacy);
  readonly maxBackfillDays = computed(() => this._catalog().maxBackfillDays);

  /** True when the last sync stored nothing — a 200 that still needs saying out loud. */
  readonly lastSyncRejectedAll = computed(
    () => this.sync.succeeded() && !healthSyncLanded(this._result()) && this._result().rejected.length > 0,
  );

  /**
   * `GET /health/metrics` — §5.1 says call it on first sync and cache, so adding
   * a metric server-side needs no release. Cached for the session.
   */
  async ensureCatalog(): Promise<OrgHealthCatalog> {
    if (!this.platform.isBrowser) return this._catalog();
    if (this._loadedAt() > 0 || this.catalogLoad.loading()) return this._catalog();
    const catalog = await runOrgAction(this.catalogLoad, this._catalogError, this.errors, () =>
      firstValueFromScoped(this.repo.getHealthCatalog(), this.destroyed),
    );
    if (catalog) {
      this._catalog.set(catalog);
      this._loadedAt.set(Date.now());
    }
    return this._catalog();
  }

  /**
   * Submit daily totals. Batched to the catalogue's `max_batch` so a long
   * backfill syncs instead of tripping the 1–200 validation, and the per-batch
   * results are folded into one — with each rejection's `index` rebased onto the
   * ORIGINAL array the caller passed, since §5.2 defines it against that.
   *
   * Returns the folded result; callers read `accepted` / `rejected` rather than
   * assuming the 200 meant anything.
   */
  async syncSamples(samples: readonly OrgHealthSample[]): Promise<OrgHealthSyncResult> {
    if (!samples.length) return EMPTY_HEALTH_SYNC_RESULT;
    // Same guard `ensureCatalog` carries, and for a sharper reason: on the server
    // `AppHttp` returns NEVER, so `firstValueFromScoped` would never settle and the
    // await would hang the render rather than fail it. A sample is a browser event.
    if (!this.platform.isBrowser) return EMPTY_HEALTH_SYNC_RESULT;
    const catalog = await this.ensureCatalog();
    const batches = chunkHealthSamples(samples, catalog.maxBatch);

    let accepted = 0;
    const rejected: { index: number; reason: string }[] = [];
    const credited = new Set<string>();
    let offset = 0;

    for (const batch of batches) {
      const batchOffset = offset;
      offset += batch.length;
      const result = await runOrgAction(this.sync, this._syncError, this.errors, () =>
        firstValueFromScoped(this.repo.syncHealthSamples(batch), this.destroyed),
      );
      if (!result) break; // a transport failure — stop, keep what already landed
      accepted += result.accepted;
      for (const row of result.rejected) rejected.push({ index: batchOffset + row.index, reason: row.reason });
      for (const metric of result.creditedMetrics) credited.add(metric);
    }

    const folded: OrgHealthSyncResult = { accepted, rejected, creditedMetrics: [...credited] };
    this._result.set(folded);
    return folded;
  }

  clearSyncError(): void {
    this._syncError.set(null);
    this.sync.reset();
  }
}
